import { Reveal, SectionLabel } from "./Reveal";
import {
  Home, Wrench, HeartPulse, Car, Scale, Dumbbell,
  X, Check,
} from "lucide-react";

const INDUSTRIES = [
  { icon: Home, title: "Real Estate", desc: "Qualify leads, schedule showings, and follow up automatically across every listing." },
  { icon: Wrench, title: "Home Services", desc: "After-hours dispatch and quote follow-up for HVAC, plumbing, and roofing — never miss a job." },
  { icon: HeartPulse, title: "Clinics & Med-Spas", desc: "Booking, reminders, and intake — cut no-shows with automatic re-confirmations." },
  { icon: Car, title: "Auto Dealerships", desc: "Test-drive bookings, service reminders, and finance pre-quals across locations." },
  { icon: Scale, title: "Legal & Professional", desc: "Intake forms, screening, and consultation booking straight onto your calendar." },
  { icon: Dumbbell, title: "Coaching & Fitness", desc: "Lead intake, trial-to-member conversion, and check-ins that know every member by name." },
];

const OLD_WAY = [
  "A separate answering service",
  "Automation tools that break (Zapier/Make)",
  "A standalone website chatbot",
  "Voicemails no one returns",
  "5 logins, 5 monthly bills",
];

const NEW_WAY = [
  "Natural, instant voice answering",
  "Remembers every customer interaction",
  "Books straight into your calendar",
  "Replies to texts, DMs & web chat",
  "One simple platform, one bill",
];

export const Industries = () => {
  return (
    <>
      {/* Industries */}
      <section id="industries" data-testid="industries-section" className="relative py-24 md:py-32 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal className="max-w-2xl">
            <SectionLabel>Industries</SectionLabel>
            <h2 className="font-heading mt-4 text-3xl md:text-5xl font-bold tracking-tight text-white">
              Built for your kind of business.
            </h2>
            <p className="mt-5 text-base md:text-lg text-white/60">
              Every business books, sells, and follows up differently. 5B AI comes
              ready-tuned for the work you actually do.
            </p>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {INDUSTRIES.map((it, i) => (
              <Reveal key={it.title} delay={(i % 3) * 0.08}>
                <div className="group h-full rounded-2xl border border-white/10 bg-[#0c0c0c] p-7 transition-all duration-300 hover:-translate-y-1 hover:border-brand/40 hover:shadow-[0_8px_32px_rgba(255,92,0,0.12)]">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand/10 border border-brand/20">
                    <it.icon className="h-6 w-6 text-brand" />
                  </span>
                  <h3 className="mt-5 font-heading text-xl font-semibold text-white">{it.title}</h3>
                  <p className="mt-2 text-sm text-white/55 leading-relaxed">{it.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <p className="mt-10 text-center text-sm text-white/45">
              Don't see your field? 5B AI adapts to any workflow — bring your own scripts and it shapes itself around them.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Comparison */}
      <section id="compare" data-testid="compare-section" className="relative py-24 md:py-32 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal className="text-center max-w-2xl mx-auto">
            <SectionLabel>Stop duct-taping software</SectionLabel>
            <h2 className="font-heading mt-4 text-3xl md:text-5xl font-bold tracking-tight text-white">
              Everything in one place.
            </h2>
            <p className="mt-5 text-base md:text-lg text-white/60">
              Most businesses stitch together five tools that don't talk to each other.
              5B AI replaces the whole stack.
            </p>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Reveal>
              <div className="h-full rounded-2xl border border-red-500/20 bg-red-950/10 p-8">
                <div className="flex items-baseline justify-between mb-6">
                  <h3 className="font-heading text-xl font-semibold text-white/80">The old way</h3>
                  <span className="font-mono text-sm text-red-400">$1,000+/mo</span>
                </div>
                <ul className="space-y-4">
                  {OLD_WAY.map((t) => (
                    <li key={t} className="flex items-center gap-3 text-white/55">
                      <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-red-500/15">
                        <X className="h-3.5 w-3.5 text-red-400" />
                      </span>
                      <span className="text-sm">{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="relative h-full rounded-2xl border border-brand/40 bg-[#0c0c0c] p-8 overflow-hidden shadow-[0_0_40px_rgba(255,92,0,0.1)]">
                <div className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-brand/20 blur-[80px]" />
                <div className="relative flex items-baseline justify-between mb-6">
                  <h3 className="font-heading text-xl font-semibold text-white">5B AI</h3>
                  <span className="font-mono text-sm text-brand">One platform</span>
                </div>
                <ul className="relative space-y-4">
                  {NEW_WAY.map((t) => (
                    <li key={t} className="flex items-center gap-3 text-white">
                      <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-brand/20">
                        <Check className="h-3.5 w-3.5 text-brand" />
                      </span>
                      <span className="text-sm">{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

    </>
  );
};
